// 전역 신규 주문 알림 프로바이더.
// 관리자 콘솔의 어느 화면이든(새로고침 없이) 신규 주문을 감지해 소리 알람을 울리고,
// 사이드바 "주문" 메뉴 옆 신규 건수 배지를 위한 receivedCount 를 제공한다.
//  - 셸(AdminShell) 안쪽에 마운트되어 인증된 모든 페이지에서 항상 폴링한다.
//  - OrdersPage 가 쓰는 것과 동일한 queryKey(["admin-orders-all"]) 라 react-query 가 캐시를 공유한다.
//  - 소리: 사용자가 준 mp3(/sounds/new-order.mp3) 를 재생하되, 없으면 Web Audio 비프음으로 자동 대체.
//  - 브라우저 자동재생 정책상 사용자 제스처(첫 진입 동의 모달의 "켜기")로 오디오를 활성화한 뒤 동작한다.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useConfirm } from "@oo/ui";
import { ORDER_STATUS, ORDER_TYPE } from "@oo/shared";
import { listAdminOrders } from "@/features/orders/ordersApi.ts";
import { subscribeAdminPush } from "@/features/push/pushClient.ts";

// 신규 주문 사운드 알람 컨트롤러 (버튼/동의 모달이 공유).
export interface OrderSoundAlarm {
  enabled: boolean;
  toggle: () => Promise<void>;
  enable: () => Promise<void>;
}

interface NewOrdersContextValue {
  // 신규(미수락) 주문 수 - 배지/표시용.
  receivedCount: number;
  alarm: OrderSoundAlarm;
}

const NewOrdersContext = createContext<NewOrdersContextValue | null>(null);

// 포장/배달 신규 주문 알림음을 다르게 낸다(개선 6).
//  - 각 유형 전용 파일을 우선 재생하고, 없으면 공용 파일(new-order.mp3) → 그래도 없으면 비프음으로 대체.
//  - 비프음 폴백도 유형별로 주파수를 달리해 소리만으로 구분되게 한다.
const PICKUP_SOUND_URLS = ["/sounds/new-order-pickup.mp3", "/sounds/new-order.mp3"];
const DELIVERY_SOUND_URLS = ["/sounds/new-order-delivery.mp3", "/sounds/new-order.mp3"];
const PICKUP_BEEP_HZ = 880; // 높은 음 = 포장
const DELIVERY_BEEP_HZ = 523; // 낮은 음 = 배달
// 소리 알람 on/off 설정을 기기에 영구 저장하는 키(새로고침/브라우저 재시작 후 복원용).
const ALARM_PREF_KEY = "oo-admin-alarm-on";

// localStorage 안전 래퍼(프라이빗 모드/차단 환경에서 예외가 나도 앱이 죽지 않게).
function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 저장 불가 환경은 무시(세션 한정으로만 동작).
  }
}

// 한 유형(포장/배달)의 소리 채널 상태. 파일 엘리먼트 + 폴백 진행 상태 + 비프 타이머를 묶는다.
interface SoundChannel {
  audio: HTMLAudioElement | null;
  // 시도 중인 url 인덱스(전용 → 공용 순으로 폴백).
  urlIndex: number;
  // 모든 파일이 실패해 비프음으로만 동작.
  fileBroken: boolean;
  beepTimer: ReturnType<typeof setInterval> | null;
  // 비동기 재생 실패가 뒤늦게 도착해 이미 처리한 주문의 알람을 다시 켜지 않게 한다.
  active: boolean;
  generation: number;
  beepNodes: Set<{ oscillator: OscillatorNode; gain: GainNode }>;
}

function newChannel(): SoundChannel {
  return {
    audio: null,
    urlIndex: 0,
    fileBroken: false,
    beepTimer: null,
    active: false,
    generation: 0,
    beepNodes: new Set(),
  };
}

// 신규 주문 소리 알람 훅(포장/배달 분리). 파일 재생(우선) + 비프음(폴백) + 신규 존재 동안 반복.
// 켜짐 설정은 localStorage 에 저장해 새로고침/재시작해도 유지하고, 브라우저 자동재생
// 정책상 새 로드 직후엔 첫 사용자 제스처(클릭/키입력)에서 오디오 잠금을 자동 해제한다.
function useOrderSoundAlarm(pickupCount: number, deliveryCount: number): OrderSoundAlarm {
  // 저장된 설정으로 초기화(없으면 꺼짐).
  const [enabled, setEnabled] = useState<boolean>(() => readLocalStorage(ALARM_PREF_KEY) === "1");
  // 오디오 잠금 해제 여부(자동재생 정책). 제스처 후 true → 그때부터 재생 가능.
  const [unlocked, setUnlocked] = useState(false);
  // 비프음 폴백용 AudioContext.
  const ctxRef = useRef<AudioContext | null>(null);
  // 포장/배달 채널.
  const pickupRef = useRef<SoundChannel>(newChannel());
  const deliveryRef = useRef<SoundChannel>(newChannel());
  // 잠금 해제를 중복 실행하거나 화면이 사라진 뒤 완료된 초기화가 알람을 되살리지 않게 한다.
  const setupRef = useRef<Promise<boolean> | null>(null);
  const setupGenerationRef = useRef(0);

  // 설정 변경 시 기기에 저장(다음 로드에서 복원).
  useEffect(() => {
    writeLocalStorage(ALARM_PREF_KEY, enabled ? "1" : "0");
  }, [enabled]);

  // 비프음 1회 (Web Audio 오실레이터, 유형별 주파수). 파일이 없을 때의 폴백.
  const beep = (ch: SoundChannel, hz: number) => {
    const ctx = ctxRef.current;
    if (!ch.active || !ctx || ctx.state !== "running") {
      return;
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = hz;
    gain.gain.value = 0.001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const nodes = { oscillator: osc, gain };
    ch.beepNodes.add(nodes);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
      ch.beepNodes.delete(nodes);
    };
    const now = ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc.start(now);
    osc.stop(now + 0.42);
  };

  // 한 채널 재생 시작: 파일 우선, 실패하면 다음 url, 모두 실패하면 비프 반복.
  const startChannel = (ch: SoundChannel, urls: string[], hz: number) => {
    ch.active = true;
    const generation = ++ch.generation;
    if (ch.audio && !ch.fileBroken) {
      ch.audio.loop = true;
      ch.audio.currentTime = 0;
      void ch.audio.play().catch(() => {
        if (!ch.active || ch.generation !== generation) {
          return;
        }
        // 현재 url 재생 실패 → 다음 url 시도, 없으면 비프.
        if (ch.urlIndex < urls.length - 1 && ch.audio) {
          ch.urlIndex += 1;
          ch.audio.src = urls[ch.urlIndex]!;
          startChannel(ch, urls, hz);
        } else {
          ch.fileBroken = true;
          startBeepLoop(ch, hz);
        }
      });
    } else {
      startBeepLoop(ch, hz);
    }
  };

  // 비프음 폴백 반복(파일 불가 시). 유형별 주파수로 반복한다.
  const startBeepLoop = (ch: SoundChannel, hz: number) => {
    const ctx = ctxRef.current;
    if (!ctx || ch.beepTimer || !ch.active) {
      return;
    }
    const generation = ch.generation;
    // 대기 중에는 오디오 포커스를 놓고, 실제 비프 알람이 필요한 동안에만 컨텍스트를 재개한다.
    void ctx.resume().then(
      () => {
        if (!ch.active || ch.generation !== generation || ch.beepTimer) {
          return;
        }
        beep(ch, hz);
        ch.beepTimer = setInterval(() => beep(ch, hz), 2500);
      },
      () => {
        // OS가 재개를 거부하면 무한 재시도하지 않는다. 별도 웹 푸시 수신 경로는 유지한다.
      },
    );
  };

  // 한 채널 정지: 파일 일시정지+되감기, 비프 타이머 정리.
  const stopChannel = (ch: SoundChannel) => {
    ch.active = false;
    ch.generation += 1;
    if (ch.audio) {
      ch.audio.pause();
      ch.audio.currentTime = 0;
    }
    if (ch.beepTimer) {
      clearInterval(ch.beepTimer);
      ch.beepTimer = null;
    }
    // 타이머만 지우면 현재 울리는 비프가 남으므로 진행 중인 노드도 즉시 정리한다.
    for (const { oscillator, gain } of ch.beepNodes) {
      oscillator.onended = null;
      oscillator.stop();
      oscillator.disconnect();
      gain.disconnect();
    }
    ch.beepNodes.clear();
  };

  // 파일 알람만 재생하거나 미처리 주문이 없을 때 비프용 오디오 포커스를 불필요하게 잡지 않는다.
  const suspendIdleContext = async () => {
    const ctx = ctxRef.current;
    const needsBeep = [pickupRef.current, deliveryRef.current].some(
      (ch) => ch.active && ch.fileBroken,
    );
    if (!ctx || ctx.state === "closed" || needsBeep) {
      return;
    }
    try {
      // 직전 resume이 아직 완료되지 않아도 suspend를 이어서 요청해 대기 전환 순서를 보장한다.
      await ctx.suspend();
    } catch {
      // 종료된 컨텍스트는 다음 초기화에서 다시 준비한다. 주문 채널 정지는 이미 완료됐다.
    }
  };

  const wantsPickup = enabled && unlocked && pickupCount > 0;
  const wantsDelivery = enabled && unlocked && deliveryCount > 0;

  // enabled + 잠금해제 + 해당 유형 신규 주문이 있는 동안 그 유형의 소리를 반복한다.
  // 두 유형 모두 신규면 두 소리가 함께 울려 어떤 유형이 들어왔는지 구분된다.
  useEffect(() => {
    if (wantsPickup) {
      if (!pickupRef.current.active) {
        startChannel(pickupRef.current, PICKUP_SOUND_URLS, PICKUP_BEEP_HZ);
      }
    } else {
      stopChannel(pickupRef.current);
    }
    if (wantsDelivery) {
      if (!deliveryRef.current.active) {
        startChannel(deliveryRef.current, DELIVERY_SOUND_URLS, DELIVERY_BEEP_HZ);
      }
    } else {
      stopChannel(deliveryRef.current);
    }
    void suspendIdleContext();
    // 화면이 꺼져도 멈추지 않는다. 건수가 변해도 미처리 주문이 남은 채널은 계속 재생한다.
    // 헬퍼는 ref 기반이라 의존성에 넣지 않는다.
  }, [wantsPickup, wantsDelivery]);

  // 로그아웃 등으로 프로바이더 자체가 제거될 때는 누수와 알람 잔류를 막기 위해 자원을 해제한다.
  useEffect(() => {
    return () => {
      setupGenerationRef.current += 1;
      stopChannel(pickupRef.current);
      stopChannel(deliveryRef.current);
      const ctx = ctxRef.current;
      ctxRef.current = null;
      if (ctx && ctx.state !== "closed") {
        void ctx.close().catch(() => {
          // 브라우저가 먼저 종료한 경우는 허용한다. 채널과 타이머는 이미 정지했다.
        });
      }
    };
  }, []);

  // 한 채널의 오디오 엘리먼트 준비 + 제스처 안에서 무음 1회 재생으로 잠금 해제.
  const unlockChannel = async (ch: SoundChannel, urls: string[], generation: number) => {
    if (!ch.audio) {
      const el = new Audio(urls[0]);
      el.preload = "auto";
      ch.audio = el;
    }
    try {
      const el = ch.audio;
      el.volume = 0;
      await el.play();
      el.pause();
      el.currentTime = 0;
      el.volume = 1;
    } catch {
      if (generation !== setupGenerationRef.current) {
        return;
      }
      // 전용 파일이 없으면 다음 url(공용)로 한 번 더 시도, 그래도 실패면 비프.
      if (ch.urlIndex < urls.length - 1) {
        ch.urlIndex += 1;
        ch.audio.src = urls[ch.urlIndex]!;
        try {
          ch.audio.volume = 0;
          await ch.audio.play();
          ch.audio.pause();
          ch.audio.currentTime = 0;
          ch.audio.volume = 1;
          return;
        } catch {
          // 떨어지면 아래에서 broken 처리.
        }
      }
      ch.fileBroken = true;
      ch.audio.volume = 1;
    }
  };

  // 사용자 제스처 시점에 오디오를 활성화(자동재생 정책 충족). 성공 시 unlocked=true.
  const ensureAudio = async (): Promise<boolean> => {
    if (unlocked) {
      return true;
    }
    if (setupRef.current) {
      return setupRef.current;
    }
    const generation = setupGenerationRef.current;
    const setup = async (): Promise<boolean> => {
      try {
        // 비프음 폴백용 컨텍스트 준비.
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctor) {
          if (!ctxRef.current) {
            ctxRef.current = new Ctor();
          }
          await ctxRef.current.resume();
          // 파일 로딩이 오래 걸려도 비프 엔진이 그동안 음악의 오디오 포커스를 잡지 않게 한다.
          await suspendIdleContext();
        }
        if (generation !== setupGenerationRef.current) {
          return false;
        }
        await unlockChannel(pickupRef.current, PICKUP_SOUND_URLS, generation);
        if (generation !== setupGenerationRef.current) {
          return false;
        }
        await unlockChannel(deliveryRef.current, DELIVERY_SOUND_URLS, generation);
        if (generation !== setupGenerationRef.current) {
          return false;
        }
        // 자동재생 허용은 유지하면서 초기화만으로 음악이 계속 작아지지 않도록 대기 상태로 돌린다.
        await suspendIdleContext();
        const filesOk = !pickupRef.current.fileBroken || !deliveryRef.current.fileBroken;
        const ok = !!ctxRef.current || filesOk;
        if (ok && generation === setupGenerationRef.current) {
          setUnlocked(true);
        }
        return ok;
      } catch {
        await suspendIdleContext();
        return false;
      }
    };
    setupRef.current = setup();
    try {
      return await setupRef.current;
    } finally {
      setupRef.current = null;
    }
  };

  // 새 로드에서 설정이 켜져 있는데 아직 잠금 해제 전이면, 첫 사용자 제스처에서 자동으로 오디오를 연다.
  // (브라우저 자동재생 정책상 제스처 없이는 소리를 낼 수 없어, 관리자가 화면을 처음 만지는 순간 풀린다.)
  useEffect(() => {
    if (!enabled || unlocked) {
      return;
    }
    const handler = () => {
      void ensureAudio();
    };
    window.addEventListener("pointerdown", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
    // ensureAudio 는 ref/세터 기반이라 의존성에 넣지 않는다.
  }, [enabled, unlocked]);

  const enable = async () => {
    // 제스처(켜기 클릭) 시점에 잠금 해제 + 설정 켜기.
    if (await ensureAudio()) {
      setEnabled(true);
    } else {
      // 오디오를 못 열어도 설정 자체는 켠다(다음 제스처에서 자동 해제 시도).
      setEnabled(true);
    }
  };

  const toggle = async () => {
    if (enabled) {
      setEnabled(false);
      return;
    }
    await enable();
  };

  return { enabled, toggle, enable };
}

// 로그인 후 콘솔 첫 진입 시 1회 노출되는 알림/소리 켜기 동의 모달.
// 동의 시 소리 알람 + 신규주문 푸시를 켠다. 한 번 물으면 다시 묻지 않는다(기기 플래그).
function AlertConsentPrompt({ alarm }: { alarm: OrderSoundAlarm }) {
  const confirm = useConfirm();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) {
      return;
    }
    if (localStorage.getItem("oo-admin-alert-asked")) {
      return;
    }
    handledRef.current = true;
    localStorage.setItem("oo-admin-alert-asked", "1");

    void (async () => {
      const ok = await confirm({
        title: "신규 주문 알림을 켤까요?",
        description:
          "신규 주문 푸시 알림과 소리 알람을 켜면 주문이 들어올 때 바로 확인할 수 있어요. 화면 상단에서 언제든 끌 수 있습니다.",
        confirmLabel: "켜기",
        cancelLabel: "다음에",
        tone: "brand",
      });
      if (ok) {
        await alarm.enable();
        await subscribeAdminPush();
      }
    })();
  }, [confirm, alarm]);

  return null;
}

// 전역 신규 주문 프로바이더. 셸 안쪽에 한 번만 마운트한다.
export function NewOrdersProvider({ children }: { children: ReactNode }) {
  // 전체 주문 폴링(OrdersPage 와 캐시 공유). 어느 화면이든 새로고침 없이 신규 주문을 받는다.
  const ordersQuery = useQuery({
    queryKey: ["admin-orders-all"],
    queryFn: () => listAdminOrders({}),
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  // 신규(미수락) 주문을 포장/배달로 나눠 센다(개선 6: 유형별 소리).
  const received = (ordersQuery.data ?? []).filter((o) => o.status === ORDER_STATUS.RECEIVED);
  const pickupCount = received.filter((o) => o.type === ORDER_TYPE.PICKUP).length;
  const deliveryCount = received.filter((o) => o.type === ORDER_TYPE.DELIVERY).length;
  const receivedCount = received.length;

  const alarm = useOrderSoundAlarm(pickupCount, deliveryCount);

  return (
    <NewOrdersContext.Provider value={{ receivedCount, alarm }}>
      <AlertConsentPrompt alarm={alarm} />
      {children}
    </NewOrdersContext.Provider>
  );
}

// 전역 신규 주문 상태/알람 소비 훅. 프로바이더 밖에서 쓰면 안전한 기본값을 반환한다.
export function useNewOrders(): NewOrdersContextValue {
  const ctx = useContext(NewOrdersContext);
  if (!ctx) {
    return {
      receivedCount: 0,
      alarm: { enabled: false, toggle: async () => {}, enable: async () => {} },
    };
  }
  return ctx;
}
